<script setup>
import { computed, h, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  createColumnHelper,
  getCoreRowModel,
  useVueTable,
} from '@tanstack/vue-table';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/PaginationDetail.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import Spinner from 'shared/components/Spinner.vue';

const store = useStore();
const { t } = useI18n();

const filters = computed(() => {
  return (
    store.getters['aiSummaries/getAiSummaryFilters'] || {
      inboxId: '',
      status: 'all',
      datePreset: 'last_7_days',
      customRange: [],
      perPage: 20,
    }
  );
});
const pagination = computed(() => {
  return (
    store.getters['aiSummaries/getAiSummaryPagination'] || {
      currentPage: 1,
      perPage: 20,
      totalPages: 0,
      totalCount: 0,
    }
  );
});
const conversations = computed(
  () => store.getters['aiSummaries/getAiSummaryConversations'] || []
);
const uiFlags = computed(() => {
  return (
    store.getters['aiSummaries/getAiSummaryUIFlags'] || {
      isLoading: false,
      isRegenerating: false,
      regeneratingId: null,
    }
  );
});
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const filtersReady = computed(() => {
  return Boolean(store.getters['aiSummaries/getAiSummaryFiltersLoaded']);
});

const statusOptions = [
  { id: 'all', label: t('BULK_SUMMARY_REPORTS.FILTERS.ALL_STATUSES') },
  { id: 'open', label: t('BULK_SUMMARY_REPORTS.STATUS_OPTIONS.OPEN') },
  { id: 'pending', label: t('BULK_SUMMARY_REPORTS.STATUS_OPTIONS.PENDING') },
  { id: 'resolved', label: t('BULK_SUMMARY_REPORTS.STATUS_OPTIONS.RESOLVED') },
];

const dateOptions = [
  { id: 'last_7_days', label: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS') },
  { id: 'last_30_days', label: t('REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS') },
  { id: 'custom', label: t('REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE') },
];

const perPageOptions = [10, 20, 50, 100];

const statusLabel = status => {
  if (!status || status === 'all') {
    return t('BULK_SUMMARY_REPORTS.FILTERS.ALL_STATUSES');
  }
  return t(`BULK_SUMMARY_REPORTS.STATUS_OPTIONS.${status.toUpperCase()}`);
};

const columnHelper = createColumnHelper();
const currentAccountId = computed(() => store.getters.getCurrentAccountId);

const table = useVueTable({
  get data() {
    return conversations.value;
  },
  get columns() {
    return [
      columnHelper.accessor('display_id', {
        header: () => t('BULK_SUMMARY_REPORTS.TABLE.HEADER.CONVERSATION'),
        size: 140,
        cell: cell => {
          const displayId = cell.getValue();
          const conversation = cell.row.original;
          const accountId = currentAccountId.value;
          return h(
            'a',
            {
              class:
                'font-semibold text-primary-600 hover:text-primary-700 hover:underline',
              href: `/app/accounts/${accountId}/conversations/${conversation.id || displayId}`,
              target: '_blank',
              rel: 'noopener noreferrer',
            },
            `#${displayId}`
          );
        },
      }),
      columnHelper.accessor('contact_name', {
        header: () => t('BULK_SUMMARY_REPORTS.TABLE.HEADER.CONTACT'),
        size: 200,
        cell: cell => cell.getValue() || '—',
      }),
      columnHelper.accessor('inbox_name', {
        header: () => t('BULK_SUMMARY_REPORTS.TABLE.HEADER.INBOX'),
        size: 200,
        cell: cell => cell.getValue() || '—',
      }),
      columnHelper.accessor('summary', {
        header: () => t('BULK_SUMMARY_REPORTS.TABLE.HEADER.SUMMARY'),
        size: 600,
        cell: cell => {
          const value =
            cell.getValue() || t('BULK_SUMMARY_REPORTS.EMPTY_SUMMARY');
          return h(
            'p',
            {
              class:
                'text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line break-words max-w-none',
            },
            value
          );
        },
      }),
      columnHelper.accessor('status', {
        header: () => t('BULK_SUMMARY_REPORTS.TABLE.HEADER.STATUS'),
        size: 160,
        cell: cell => {
          const map = {
            open: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
            pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200',
            resolved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200',
          };
          const value = cell.getValue();
          const cls = map[value] || 'bg-slate-100 text-slate-700 dark:bg-slate-800';
          return h(
            'span',
            {
              class: `inline-flex px-3 py-1 rounded-full text-xs font-semibold ${cls}`,
            },
            statusLabel(value)
          );
        },
      }),
    ];
  },
  manualPagination: true,
  getCoreRowModel: getCoreRowModel(),
  state: {
    get pagination() {
      return {
        pageIndex: (pagination.value.currentPage || 1) - 1,
        pageSize: pagination.value.perPage,
      };
    },
  },
  get rowCount() {
    return pagination.value?.totalCount || conversations.value.length;
  },
  onPaginationChange: updater => {
    const next = updater({
      pageIndex: (pagination.value.currentPage || 1) - 1,
      pageSize: pagination.value.perPage,
    });
    handlePageChange(next.pageIndex + 1);
  },
});

const handleGenerate = async () => {
  if (uiFlags.value.isLoading) {
    return;
  }
  try {
    await store.dispatch('aiSummaries/generateAiSummaries', { page: 1 });
    useAlert(t('BULK_SUMMARY_REPORTS.SUCCESS'), { type: 'success' });
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('BULK_SUMMARY_REPORTS.ERROR.MESSAGE'),
      { type: 'error' }
    );
  }
};

const handleForceRegenerate = async () => {
  if (!conversations.value.length) return;
  try {
    await store.dispatch('aiSummaries/generateAiSummaries', {
      page: pagination.value.currentPage || 1,
      forceRegenerate: true,
    });
    useAlert(t('BULK_SUMMARY_REPORTS.ALERTS.REGENERATE_ALL_SUCCESS'), {
      type: 'success',
    });
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('BULK_SUMMARY_REPORTS.ALERTS.REGENERATE_ALL_ERROR'),
      { type: 'error' }
    );
  }
};

const handlePageChange = page => {
  store.dispatch('aiSummaries/changeAiSummaryPage', page);
};

const updateFilters = payload => {
  store.dispatch('aiSummaries/updateAiSummaryFilters', payload);
  store.dispatch('aiSummaries/generateAiSummaries', { page: 1 });
};

const handlePerPageChange = value => {
  updateFilters({ perPage: value });
  if (conversations.value.length) {
    store.dispatch('aiSummaries/generateAiSummaries', { page: 1 });
  }
};

const shouldShowCustomRange = computed(
  () => filters.value.datePreset === 'custom'
);

onMounted(async () => {
  store.dispatch('aiSummaries/initialize');
  await store.dispatch('aiSummaries/generateAiSummaries', { page: 1 });
  if (!inboxes.value.length) {
    await store.dispatch('inboxes/get');
  }
});
</script>

<template>
  <div class="flex flex-col gap-5">
    <section class="rounded-2xl border border-n-weak bg-white/80 p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/30">
      <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 class="text-xl font-semibold text-n-slate-12 dark:text-white">
            {{ $t('BULK_SUMMARY_REPORTS.HEADER') }}
          </h2>
          <p class="text-sm text-n-gray-6 dark:text-n-gray-3">
            {{ $t('BULK_SUMMARY_REPORTS.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex flex-wrap gap-2">
          <woot-button
            color-scheme="secondary"
            variant="smooth"
            :disabled="!filtersReady || !conversations.length || uiFlags.isLoading"
            @click="handleForceRegenerate"
          >
            {{ $t('BULK_SUMMARY_REPORTS.REGENERATE_ALL') }}
          </woot-button>
          <woot-button
            variant="smooth"
            color-scheme="primary"
            :loading="uiFlags.isLoading"
            :disabled="!filtersReady"
            @click="handleGenerate"
          >
            {{ $t('BULK_SUMMARY_REPORTS.GENERATE_SUMMARY') }}
          </woot-button>
        </div>
      </div>

      <div class="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div>
          <label class="filter-label">{{
            $t('BULK_SUMMARY_REPORTS.FILTERS.INBOX')
          }}</label>
          <select
            class="form-select mt-1"
            :value="filters.inboxId"
            @change="event => updateFilters({ inboxId: event.target.value })"
          >
            <option value="">{{ $t('BULK_SUMMARY_REPORTS.FILTERS.ALL_INBOXES') }}</option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
        </div>

        <div>
          <label class="filter-label">{{
            $t('BULK_SUMMARY_REPORTS.FILTERS.STATUS')
          }}</label>
          <select
            class="form-select mt-1"
            :value="filters.status"
            @change="event => updateFilters({ status: event.target.value })"
          >
            <option
              v-for="option in statusOptions"
              :key="option.id"
              :value="option.id"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <div>
          <label class="filter-label">{{
            $t('BULK_SUMMARY_REPORTS.FILTERS.DATE_RANGE')
          }}</label>
          <select
            class="form-select mt-1"
            :value="filters.datePreset"
            @change="event => updateFilters({ datePreset: event.target.value })"
          >
            <option
              v-for="option in dateOptions"
              :key="option.id"
              :value="option.id"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <div>
          <label class="filter-label">{{
            $t('BULK_SUMMARY_REPORTS.FILTERS.PER_PAGE')
          }}</label>
          <select
            class="form-select mt-1"
            :value="filters.perPage"
            @change="event => handlePerPageChange(Number(event.target.value))"
          >
            <option v-for="option in perPageOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="shouldShowCustomRange" class="mt-4">
        <label class="filter-label">
          {{ $t('BULK_SUMMARY_REPORTS.FILTERS.CUSTOM_RANGE') }}
        </label>
        <WootDateRangePicker
          class="mt-2"
          :value="filters.customRange"
          show-range
          :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
          @change="value => updateFilters({ customRange: value })"
        />
      </div>
    </section>

    <section class="rounded-2xl border border-n-weak bg-white/85 p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/30">
      <div v-if="uiFlags.isLoading" class="flex flex-col items-center justify-center py-16">
        <Spinner />
        <p class="mt-3 text-sm text-n-gray-6 dark:text-n-gray-3">
          {{ $t('BULK_SUMMARY_REPORTS.LOADING') }}
        </p>
      </div>

      <div v-else-if="!conversations.length">
        <div class="text-center py-12">
          <fluent-icon
            icon="chat-bubbles"
            size="48"
            class="mx-auto text-n-gray-4 dark:text-n-gray-6"
          />
          <h3 class="mt-3 text-base font-semibold text-n-slate-12 dark:text-white">
            {{ $t('BULK_SUMMARY_REPORTS.EMPTY_STATE.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-gray-6 dark:text-n-gray-3">
            {{ $t('BULK_SUMMARY_REPORTS.EMPTY_STATE.MESSAGE') }}
          </p>
        </div>
      </div>

      <div v-else class="flex flex-col gap-4">
        <Table :table="table" :borderless="true" minimal />
        <Pagination :table="table" />
      </div>
    </section>
  </div>
</template>

<style scoped>
.filter-label {
  @apply text-xs font-semibold tracking-wide text-n-gray-5 uppercase dark:text-n-gray-3;
}

.form-select {
  @apply block w-full rounded-lg border border-n-weak bg-white px-3 py-2 text-sm text-n-slate-12 dark:border-white/10 dark:bg-slate-900 dark:text-white focus:border-primary-500 focus:outline-none;
}

.line-clamp-4 {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
